/**
 * Atom: Input
 *
 * Variants: default | search
 * Supports label, error state, left/right icons.
 *
 * Usage:
 *   <Input label="Project Name" value={name} onChange={e => setName(e.target.value)} />
 *   <Input variant="search" placeholder="Search projects..." value={q} onChange={...} />
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  variant?: 'default' | 'search'
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  variant = 'default',
  leftIcon,
  rightIcon,
  style,
  ...rest
}, ref) => {
  const borderColor = error ? tokens.color.feedback.error : tokens.color.border.default

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <style>{`
        /* D1b — focus ring per v2 spec: 1px gold border + 3px gold-quiet ring */
        input.ml-input-D1b:focus,
        input.ml-input-D1b:focus-visible {
          border-color: var(--brand-gold) !important;
          box-shadow: 0 0 0 3px var(--brand-gold-quiet);
        }
        input.ml-input-D1b.ml-err:focus,
        input.ml-input-D1b.ml-err:focus-visible {
          border-color: var(--st-error) !important;
          box-shadow: 0 0 0 3px rgba(from var(--st-error) r g b / 0.18);
        }
      `}</style>
      {label && (
        <label style={{
          fontSize: 12,
          fontWeight: 500,
          color: tokens.color.text.secondary,
          fontFamily: tokens.typography.fontFamily.sans,
        }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {leftIcon && (
          <span style={{
            position: 'absolute',
            left: tokens.spacing[3],
            color: tokens.color.text.muted,
            display: 'flex',
            pointerEvents: 'none',
          }}>
            {leftIcon}
          </span>
        )}
        <input
          className={['ml-input-D1b', error ? 'ml-err' : ''].filter(Boolean).join(' ')}
          style={{
            width: '100%',
            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
            paddingLeft: leftIcon ? tokens.spacing[8] : tokens.spacing[3],
            paddingRight: rightIcon ? tokens.spacing[8] : tokens.spacing[3],
            background: tokens.color.background.elevated,
            border: `1px solid ${borderColor}`,
            borderRadius: tokens.radius.md,
            color: tokens.color.text.primary,
            fontSize: 13,
            fontFamily: tokens.typography.fontFamily.sans,
            outline: 'none',
            transition: `border-color 120ms ease, box-shadow 120ms ease`,
            boxSizing: 'border-box',
            ...style,
          }}
          ref={ref}
          {...rest}
        />
        {rightIcon && (
          <span style={{
            position: 'absolute',
            right: tokens.spacing[3],
            color: tokens.color.text.muted,
            display: 'flex',
            pointerEvents: 'none',
          }}>
            {rightIcon}
          </span>
        )}
      </div>
      {error && (
        <span style={{ fontSize: 11, color: tokens.color.feedback.error, lineHeight: 1.4 }}>
          {error}
        </span>
      )}
      {hint && !error && (
        <span style={{ fontSize: 11, color: tokens.color.text.muted, lineHeight: 1.4 }}>
          {hint}
        </span>
      )}
    </div>
  )
})

Input.displayName = "Input"
